import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type * as z from "zod";

import { orgCreate } from "@blis/shared/schemas/org";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { usTimezones } from "@/lib/utils/tz";
import { mutationResponse } from "@/utils/trpc";
import type { OrgDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { HtmlEditor } from "../HtmlEditor";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof orgCreate>;

const archiveStorageOptions = [
  { label: "Retain", value: "RETAIN" },
  { label: "Offsite storage", value: "OFFSITE" },
  { label: "Discard", value: "DISCARD" },
];

const orgTypeOptions = [
  {
    label: "Primary Lab",
    value: "PRIMARY_LAB",
    description:
      "A testing lab under the primary organization providing LIS services",
  },
  {
    label: "Reference Lab",
    value: "SENDOUT_LAB",
    description: "A lab that provides testing services to the primary lab(s)",
  },
  {
    label: "Client",
    value: "CLIENT",
    description: "An external user or customer of the lab",
  },
];

type ClientDetailsFormContextValue = {
  orgType: "PRIMARY_LAB" | "SENDOUT_LAB" | "CLIENT" | null;
  setOrgType: (orgType: "PRIMARY_LAB" | "SENDOUT_LAB" | "CLIENT") => void;
};

const ClientDetailsFormContext =
  React.createContext<ClientDetailsFormContextValue>(
    {} as ClientDetailsFormContextValue,
  );

export function ClientDetailsForm({ org }: { org?: OrgDetail }) {
  const [orgType, setOrgType] = React.useState<
    "PRIMARY_LAB" | "SENDOUT_LAB" | "CLIENT" | null
  >(org?.orgType || null);

  const methods = useForm({
    defaultValues: {
      ...org,
      address: org?.address ?? "",
      city: org?.city ?? "",
      code: org?.code ?? "",
      state: org?.state ?? "",
      zip: org?.zip ?? "",
      labDirector: org?.labDirector ?? "",
      cliaNumber: org?.cliaNumber ?? "",
      invoiceEmail: org?.invoiceEmail ?? "",
      splitBillingOrgIds: org?.splitBillingOrgIds as number[] | undefined,
    },
    resolver: zodResolver(orgCreate),
  });
  const navigate = useNavigate();

  const create = trpc.admin.org.create.useMutation();
  const update = trpc.admin.org.update.useMutation();

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (org) {
      update.mutate(
        { id: org.id, ...values },
        mutationResponse({
          errorTitle: "Error updating client",
          successMessage: "Client updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error creating client",
          successMessage: "Client created",
          onSuccess(data) {
            if (data) {
              navigate({
                to: "/admin/clients/$id/edit",
                params: { id: data.id },
              });
            }
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <ClientDetailsFormContext.Provider value={{ orgType, setOrgType }}>
        <FormProvider {...methods}>
          <AdminForm onSubmit={methods.handleSubmit(onSubmit)}>
            <TextInputs />
            <ClientOptions />
            <AccessioningNotesField />
            <ClientContact />
            <LabDetails />
            <ArchiveSettings />
            <InvoiceSettings org={org} />
            <Separator />
            <div className="flex justify-end gap-2">
              <Link to="/admin/clients" variant="outline" asButton>
                Cancel
              </Link>
              <Button
                type="submit"
                isPending={create.isPending || update.isPending}
              >
                Save
              </Button>
            </div>
          </AdminForm>
        </FormProvider>
      </ClientDetailsFormContext.Provider>
    </TabLayout>
  );
}

function TextInputs() {
  const { control } = useFormContext<FormValues>();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-1 md:col-span-3"
          >
            <Label>Name</Label>
            <Input />
            <Description>Display name for the client</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name="code"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Code</Label>
            <Input />
            <Description>
              Optional internal code for the organization
            </Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name="orgType"
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-1 md:col-span-2"
            // isDisabled={!!org}
          >
            <Label>Type</Label>
            <SelectTrigger />
            <SelectList items={orgTypeOptions}>
              {(item) => (
                <SelectItem id={item.value} textValue={item.label}>
                  {item.label}
                  <Description>{item.description}</Description>
                </SelectItem>
              )}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </div>
  );
}

function ClientContact() {
  const { control, watch } = useFormContext<FormValues>();
  const orgType = watch("orgType");

  if (!orgType || orgType === "CLIENT") {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Contact</Section.Subtitle>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Controller
          control={control}
          name="address"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
              isRequired={false}
              className="col-span-1 md:col-span-4"
            >
              <Label>Address</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="city"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
              className="col-span-1 md:col-span-2"
            >
              <Label>City</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="state"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>State</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="zip"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Zip</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
              className="col-span-1 md:col-span-2"
            >
              <Label>Email</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Phone</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="fax"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Fax</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
    </div>
  );
}

function AccessioningNotesField() {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();
  const value = watch("accessioningNotes");

  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Accessioning</Section.Subtitle>
      <HtmlEditor
        id="accessioning-notes"
        label="Accessioning notes"
        description="Shown to lab staff on create accession when this client is primary. Formatting via keyboard shortcuts."
        value={value}
        error={errors.accessioningNotes?.message}
        onChange={(next) =>
          setValue("accessioningNotes", next, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
    </div>
  );
}

function ClientOptions() {
  const { control, watch } = useFormContext<FormValues>();
  const { data } = trpc.orgs.primaryLabs.useQuery();
  const primaryLabs = data || [];

  const orgType = watch("orgType");

  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Options</Section.Subtitle>
      <Controller
        control={control}
        name="primarySelectable"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-primary-selectable"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Primary client
            </Checkbox>
            <Description>
              Can be selected as a primary client. This is also used to
              determine which client is billed for tests.
            </Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="selectable"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-selectable"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Secondary client
            </Checkbox>
            <Description>
              Can be selected as an additional client. Reports will be shared
              with this client.
            </Description>
          </CheckboxField>
        )}
      />
      <Controller
        control={control}
        name="ignoreInvoices"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="check-ignore-invoices"
              isSelected={field.value}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Ignore invoices
            </Checkbox>
            <Description>
              Do not invoice this client if selected as primary
            </Description>
          </CheckboxField>
        )}
      />
      {orgType === "CLIENT" && (
        <Controller
          control={control}
          name="primaryLabId"
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              isInvalid={!!error}
              validationBehavior="aria"
              className="mt-2"
            >
              <Label>Primary lab</Label>
              <SelectTrigger />
              <SelectList items={primaryLabs}>
                {(item) => (
                  <SelectItem id={item.id} textValue={item.name}>
                    {item.name}
                  </SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
      )}
    </div>
  );
}

function ArchiveSettings() {
  const { control, watch } = useFormContext<FormValues>();
  const archiveType = watch("archiveType");

  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>Archive settings</Section.Subtitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          control={control}
          name="archiveType"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Select
              {...field}
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Storage</Label>
              <SelectTrigger />
              <SelectList items={archiveStorageOptions}>
                {(item) => (
                  <SelectItem id={item.value}>{item.label}</SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
        {archiveType && archiveType !== "RETAIN" && (
          <Controller
            name="archiveDuration"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <NumberField
                {...field}
                value={field.value ?? Number.NaN}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Duration (days)</Label>
                <NumberInput />
                <FieldError>{error?.message}</FieldError>
              </NumberField>
            )}
          />
        )}
      </div>
    </div>
  );
}

function InvoiceSettings({ org }: { org?: OrgDetail }) {
  const { control, watch } = useFormContext<FormValues>();
  const { data, isLoading } = trpc.orgs.list.useQuery();
  const orgs = data || [];

  const options = React.useMemo(() => {
    return org
      ? [
          {
            label: "None",
            value: -1,
          },
          ...orgs
            .filter((o) => o.id !== org.id)
            .map((o) => ({
              label: o.name,
              value: o.id,
            })),
        ]
      : [];
  }, [org, orgs]);

  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>Invoice settings</Section.Subtitle>
      <Controller
        name="invoiceEmail"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            type="email"
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Invoice email</Label>
            <Input />
            <Description>Email address to send invoices to</Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      {org && (
        <Controller
          control={control}
          name="splitBillingOrgIds"
          render={({
            field: { ref, value, ...field },
            fieldState: { error },
          }) => (
            <Select
              {...field}
              selectedKey={value?.[0] ?? null}
              onSelectionChange={(val) => {
                if (val === -1) {
                  field.onChange([]);
                } else {
                  field.onChange([val]);
                }
              }}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Split billing</Label>
              {value?.[0] && (
                <Description>
                  <span>
                    When <span className="font-semibold">{org.name}</span> is
                    the primary client, and{" "}
                    <span className="font-semibold">
                      {orgs.find((o) => o.id === value?.[0])?.name}
                    </span>{" "}
                    is a shared client, invoice line items will be flagged to
                    indicate split billing.
                  </span>
                </Description>
              )}

              <SelectTrigger />
              <SelectList items={options}>
                {(item) => (
                  <SelectItem id={item.value} textValue={item.label}>
                    {item.label}
                  </SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
      )}
    </div>
  );
}

function LabDetails() {
  const { control, watch } = useFormContext<FormValues>();
  const orgType = watch("orgType");

  if (orgType !== "PRIMARY_LAB" && orgType !== "SENDOUT_LAB") {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Lab details</Section.Subtitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          control={control}
          name="labDirector"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Lab director</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="physician"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Physician</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="cliaNumber"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>CLIA number</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="fdaNumber"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>FDA number</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="timezone"
          render={({ field, fieldState: { error } }) => (
            <Select
              aria-label="Time zone"
              selectedKey={field.value}
              onSelectionChange={(val) => field.onChange(val as string)}
              isInvalid={!!error}
              onBlur={field.onBlur}
              validationBehavior="aria"
            >
              <Label>Time zone</Label>
              <SelectTrigger />
              <SelectList items={usTimezones}>
                {(item) => (
                  <SelectItem
                    id={item.zone}
                    textValue={item.display?.replace("US/", "")}
                  >
                    {item.display}
                  </SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
        {/* <Controller
          control={control}
          name="reportDisplayName"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Report display name</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="requisitionDisplayName"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Requisition display name</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        /> */}
      </div>
    </div>
  );
}
